import { useEffect, useMemo } from 'react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { Graphic, MapView, Point, WebMap } from '../constructors';
import { EsriMapEntityTypeEnum } from '../types';
import type { GraphicHit } from '../types';
import { moveLayerOnTop } from '../helpers/utils';
import { FEATURE_LAYERS_PROPERTIES, EVENT_RIGHT_CLICK_BUTTON_NUMBER } from '../constants';
import { isValidLatLng } from '../../location/helpers';

type UseAddLayerClickHandlerProps = {
    hasClusters?: boolean;
    dispatchFromMap?: boolean;
    isFirstResponder?: boolean;
    createEventAtLocationPin?: boolean;
    mapView?: MapView;
    webMap?: WebMap;
    eventsLayer?: FeatureLayer;
    unitsLayer?: FeatureLayer;
    stationsLayer?: FeatureLayer;
    hospitalsLayer?: FeatureLayer;
    sensorsLayer?: FeatureLayer;
    geoPointLocationLayer?: FeatureLayer;
    phoneCallLayer?: FeatureLayer;
    amlLayer?: FeatureLayer;
    selectCluster?: (clusterId: number) => void;
    selectUnit?: (unitId: number, shouldResetZoomLevel?: boolean) => void;
    selectUnitUser?: (unitUserId: number) => void;
    selectEvent?: (eventId: number, shouldResetZoomLevel?: boolean) => void;
    selectStation?: (stationId: number) => void;
    selectGeoPointLocation?: (geoPointLocationId: number) => void;
    pinGeoPointOnMap?: (lat: number, lng: number) => void;
    selectHospital?: (hospitalId: number, shouldResetZoomLevel?: boolean) => void;
    /** Sensors have no map-ledger entry; their detail lives in a SENSOR workspace panel. */
    openSensorPanel?: (sensorId: string | undefined) => void;
    selectPhoneCall?: (phoneCallId: number) => void;
    selectAmlLocation?: (amlPhoneCallId: number) => void;
    resetMapPopupVisibilityListener: (shouldAvoidPopupOpening: boolean) => void;
    openUnitsClusterPopup: (popupLocation: Point, clusterGraphic: Graphic) => void;
    openEventContextMenu: (eventId: number) => void;
    openUnitContextMenu: (unitId: number) => void;
};

export function useAddLayerClickHandler({
    mapView,
    webMap,
    selectCluster,
    selectUnit,
    selectUnitUser,
    selectEvent,
    selectStation,
    selectHospital,
    openSensorPanel,
    selectGeoPointLocation,
    pinGeoPointOnMap,
    selectPhoneCall,
    selectAmlLocation,
    eventsLayer,
    unitsLayer,
    stationsLayer,
    hospitalsLayer,
    sensorsLayer,
    geoPointLocationLayer,
    phoneCallLayer,
    amlLayer,
    resetMapPopupVisibilityListener,
    hasClusters,
    openUnitsClusterPopup,
    dispatchFromMap,
    isFirstResponder,
    createEventAtLocationPin,
    openEventContextMenu,
    openUnitContextMenu,
}: UseAddLayerClickHandlerProps) {
    const layers = useMemo(
        () =>
            [
                eventsLayer,
                unitsLayer,
                stationsLayer,
                hospitalsLayer,
                sensorsLayer,
                geoPointLocationLayer,
                phoneCallLayer,
                amlLayer,
            ].filter((layer) => !!layer) as FeatureLayer[],
        [
            eventsLayer,
            unitsLayer,
            stationsLayer,
            hospitalsLayer,
            sensorsLayer,
            geoPointLocationLayer,
            phoneCallLayer,
            amlLayer,
        ]
    );

    useEffect(
        function addLayerListeners() {
            if (!mapView || !layers.length) {
                return;
            }

            const onClick = mapView.on('click', (event) => {
                const isRightClickOnTheMap = event.button === EVENT_RIGHT_CLICK_BUTTON_NUMBER;
                mapView.popup.visible = false;

                mapView
                    .hitTest(event, {
                        include: layers,
                    })
                    .then((response) => {
                        const layerGraphic = (response.results as GraphicHit[])[0]?.graphic;
                        if (!layerGraphic) {
                            if (
                                createEventAtLocationPin &&
                                isRightClickOnTheMap &&
                                isValidLatLng(event.mapPoint?.latitude, event.mapPoint?.longitude)
                            ) {
                                pinGeoPointOnMap?.(
                                    event.mapPoint.latitude,
                                    event.mapPoint.longitude
                                );
                                moveLayerOnTop(
                                    webMap,
                                    FEATURE_LAYERS_PROPERTIES.GEO_POINT_LOCATIONS.layerKey
                                );
                            } else if (!isRightClickOnTheMap) {
                                resetMapPopupVisibilityListener(false);
                            }
                        } else {
                            const eventsLayerWasClicked = layerGraphic.layer === eventsLayer;
                            const unitsLayerWasClicked = layerGraphic.layer === unitsLayer;
                            const stationsLayerWasClicked = layerGraphic.layer === stationsLayer;
                            const hospitalsLayerWasClicked = layerGraphic.layer === hospitalsLayer;
                            const geoPointLocationLayerWasClicked =
                                layerGraphic.layer === geoPointLocationLayer;
                            const phoneCallLayerWasClicked = layerGraphic.layer === phoneCallLayer;
                            const amlLayerWasClicked = layerGraphic.layer === amlLayer;
                            const unitsClusterWasClicked = layerGraphic.isAggregate;
                            const shouldAvoidPopupOpening =
                                eventsLayerWasClicked ||
                                stationsLayerWasClicked ||
                                hospitalsLayerWasClicked ||
                                geoPointLocationLayerWasClicked ||
                                phoneCallLayerWasClicked ||
                                amlLayerWasClicked ||
                                (hasClusters
                                    ? unitsLayerWasClicked && !unitsClusterWasClicked
                                    : unitsLayerWasClicked);
                            resetMapPopupVisibilityListener(shouldAvoidPopupOpening);

                            const contextMenuAllowed =
                                !isFirstResponder && isRightClickOnTheMap && dispatchFromMap;

                            switch (layerGraphic.layer) {
                                case eventsLayer: {
                                    if (layerGraphic?.attributes?.entityId) {
                                        moveLayerOnTop(
                                            webMap,
                                            FEATURE_LAYERS_PROPERTIES.EVENTS.layerKey
                                        );
                                        if (contextMenuAllowed) {
                                            openEventContextMenu(layerGraphic.attributes.entityId);
                                        } else {
                                            selectEvent?.(layerGraphic.attributes.entityId);
                                        }
                                    }
                                    break;
                                }
                                case unitsLayer: {
                                    moveLayerOnTop(
                                        webMap,
                                        FEATURE_LAYERS_PROPERTIES.UNITS.layerKey
                                    );
                                    if (unitsClusterWasClicked) {
                                        selectCluster?.(layerGraphic.getObjectId());
                                        openUnitsClusterPopup(event.mapPoint, layerGraphic);
                                    } else if (layerGraphic?.attributes?.entityId) {
                                        if (
                                            layerGraphic?.attributes?.type ===
                                            EsriMapEntityTypeEnum.UNIT
                                        ) {
                                            if (contextMenuAllowed) {
                                                openUnitContextMenu(
                                                    layerGraphic.attributes.entityId
                                                );
                                            } else {
                                                selectUnit?.(layerGraphic.attributes.entityId);
                                            }
                                        } else if (
                                            layerGraphic?.attributes?.type ===
                                            EsriMapEntityTypeEnum.USER
                                        ) {
                                            selectUnitUser?.(layerGraphic.attributes.entityId);
                                        }
                                    }
                                    break;
                                }
                                case stationsLayer: {
                                    if (layerGraphic?.attributes?.entityId) {
                                        moveLayerOnTop(
                                            webMap,
                                            FEATURE_LAYERS_PROPERTIES.STATIONS.layerKey
                                        );
                                        selectStation?.(layerGraphic.attributes.entityId);
                                    }
                                    // if tracking mode is enabled center map on selected station
                                    // will implement fully in later ticket
                                    // view.goTo(stationGraphic, { duration: 500, easing: 'ease-in-out' });
                                    break;
                                }
                                case hospitalsLayer: {
                                    if (layerGraphic?.attributes?.entityId) {
                                        moveLayerOnTop(
                                            webMap,
                                            FEATURE_LAYERS_PROPERTIES.HOSPITALS.layerKey
                                        );
                                        selectHospital?.(layerGraphic.attributes.entityId);
                                    }
                                    break;
                                }
                                case sensorsLayer: {
                                    // Keyed on sensorId, not entityId: entityId is a synthetic
                                    // numeric id for the Esri layer cache, while the panel and the
                                    // sensor store are keyed on the vendor's string id.
                                    if (layerGraphic?.attributes?.sensorId) {
                                        moveLayerOnTop(
                                            webMap,
                                            FEATURE_LAYERS_PROPERTIES.SENSORS.layerKey
                                        );
                                        openSensorPanel?.(layerGraphic.attributes.sensorId);
                                    }
                                    break;
                                }
                                case geoPointLocationLayer: {
                                    if (layerGraphic?.attributes?.entityId) {
                                        selectGeoPointLocation?.(layerGraphic.attributes.entityId);
                                        moveLayerOnTop(
                                            webMap,
                                            FEATURE_LAYERS_PROPERTIES.GEO_POINT_LOCATIONS.layerKey
                                        );
                                    }
                                    break;
                                }
                                case phoneCallLayer: {
                                    if (
                                        phoneCallLayerWasClicked &&
                                        layerGraphic?.attributes?.entityId
                                    ) {
                                        selectPhoneCall?.(layerGraphic.attributes.entityId);
                                    }
                                    break;
                                }
                                case amlLayer: {
                                    if (layerGraphic?.attributes?.amlPhoneCallId) {
                                        selectAmlLocation?.(layerGraphic.attributes.amlPhoneCallId);
                                    }
                                    break;
                                }
                            }
                        }
                    });
            });

            return () => onClick.remove();
        },
        [
            webMap,
            mapView,
            selectCluster,
            selectUnit,
            selectUnitUser,
            selectEvent,
            selectStation,
            selectHospital,
            openSensorPanel,
            selectGeoPointLocation,
            selectPhoneCall,
            selectAmlLocation,
            layers,
            eventsLayer,
            unitsLayer,
            stationsLayer,
            hospitalsLayer,
            sensorsLayer,
            geoPointLocationLayer,
            phoneCallLayer,
            amlLayer,
            resetMapPopupVisibilityListener,
            hasClusters,
            openUnitsClusterPopup,
            dispatchFromMap,
            isFirstResponder,
            createEventAtLocationPin,
            openEventContextMenu,
            pinGeoPointOnMap,
            openUnitContextMenu,
        ]
    );
}
